import { Box, Button, ButtonGroup, Container, Grid, InputLabel } from "@material-ui/core";
import { CloseOutlined } from "@material-ui/icons";
import { parseISO } from "date-fns";
import { Formik } from "formik";
import React, { Dispatch, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Translation, useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { isUndefined, without } from "underscore";
import { selectEnableFleetVehicleScheduling, selectUseBulkVehicleAssignments } from "../../containers/FleetManager/MyProducts/ParkingRightsDataTable/common";
import useAppContext from "../../context/hooks/useAppContext";
import useFleetManagerContext from "../../context/hooks/useFleetManagerContext";
import useImportVehiclesContext from "../../context/hooks/useImportVehiclesContext";
import { AssignmentType } from "../../context/store/reducers/fleetManagerContext.reducer";
import { ProductSchedule } from "../../context/store/reducers/importVehicles.reducer";
import { parseAssignedParkingRowKey } from "../../helpers/vehicleAssignment.utils";
import { IRightSelection } from "../../hooks/useValidationSchema";
import { CountriesNumberPlates, convertIsoCountryCodeToNumberPlatePrefix, convertNumberPlatePrefixToIsoCountryCode, getCountryCodeByLanguage, moveCountryToTop } from "../../lib/countriesNumberPlates";
import { ParkingProductType } from "../../models";
import { VehicleAssignmentModel } from "../../models/delegations/DelegatedParkingProductsModel";
import { ContractType } from "../../models/ParkingProductModel";
import { AssignVehicleRequestModel } from "../../models/vehicle-assignments/AssignVehicleRequestModel";
import { ParkingProductModel } from "../../models/vehicle-assignments/ParkingProductModel";
import { UpdateVehicleRequestModel } from "../../models/vehicle-assignments/UpdateVehicleRequestModel";
import { useAppSelector } from "../../store";
import parkingProductsActions from "../../store/actions/parkingProducts.actions";
import { RootReducer } from "../../store/reducers";
import { variables } from "../../theme/variables";
import CustomButton from "../../ui/CustomButton/CustomButton";
import CustomError from "../../ui/CustomError/CustomError";
import CustomInput from "../../ui/CustomInput/CustomInput";
import Heading from "../../ui/Heading/Heading";
import { NumberPlateDeSupport } from "../../ui/NumberPlate";
import RequiredInputLabel from "../../ui/RequiredInputLabel/RequiredInputLabel";
import Spacer from "../../ui/Spacer/Spacer";
import { checkValidationError, format } from "../../utils";
import { getLocalizedDate } from "../../utils/date.utils";
import numberPlateValidator from "../../validators/numberplate.validator";
import { DeleteDelegeeIcon, DeleteText, DeleteWrapper } from "../DataTable/DataTableFilters/CommonStyledComponents";
import { NoPaddingBottomGrid } from "../DataTable/DataTableStyledComponents";
import ParkingRightsGrid from "../DelegationForm/ParkingRightsGrid/ParkingRightsGrid";
import { MuiHelperText } from "../MuiHelperText";
import SchedulingDateRangePicker, { SchedulingDateRangePickerError } from "../SchedulingDateRangePicker/SchedulingDateRangePicker";
import VehicleConcurrentUploadWarning, { useBlockVehicleConcurrentUpload } from "../VehicleConcurrentUploadWarning";
import useValidationSchema, { IVehicleFormValues } from "./hooks/useValidationSchema";
import * as parkingProductsThunks from "../../store/actions/parkingProducts.thunks";
import errorConstants from "../../constants/error.constants";

const { typography } = variables;

interface IFormProps extends StateProps, DispatchProps {
  onClose: () => void;
  onRefresh?: () => void;
}

enum Fields {
  Description = "description",
  NumberPlate = "numberPlate",
  NumberPlatePrefix = "numberPlatePrefix",
  Products = "products",
}

type ValidationResult = { status: 'valid' } | { status: 'error' | 'warning'; message: string }

const VehicleForm: React.FC<IFormProps> = (props) => {
  const {
    onClose,
    onRefresh,
    parkingProducts,
    getParkingProducts,
    assignVehicle,
    getVehicleAssignment,
    disposeError,
    updateVehicleAssignment,
  } = props;

  const { appState } = useAppContext();
  const {
    fleetManagerState,
    setAssignmentType,
    toggleDeleteVehicleAssignmentsWarning,
    setNumberPlateWarning,
    setNumberPlateError,
    setVehicleDragAndDropView
  } = useFleetManagerContext();

  const { setSelectedProducts } = useImportVehiclesContext();

  const isEditMode = fleetManagerState.isEditVehicleAssignmentMode;

  const { t } = useTranslation(["vehicleForm", "visitors", "globals", "errors"]);

  useEffect(() => {
    return () => disposeError();
  },  [disposeError]);

  const newVehicleAssignmentIdRef = useRef<string>();
  const assignedProductIdsWithoutExpiryRef = useRef<number[]>([]);
  const [isFormInitialized, setIsFormInitialized] = useState(false);
  const useBulkVehicleAssignments = useAppSelector(selectUseBulkVehicleAssignments);
  const enableFleetVehicleScheduling = useAppSelector(selectEnableFleetVehicleScheduling) && !parkingProducts.hasUnlimitedEntryRights;
  const blockVehicleConcurrentUpload = useBlockVehicleConcurrentUpload();
  const [schedulingErrors, setSchedulingErrors] = useState<Record<number, SchedulingDateRangePickerError | undefined>>({});

  const toDateOnly = (date: Date): Date => {
    const dateOnly = new Date(date);
    dateOnly.setHours(0, 0, 0, 0);
    return dateOnly;
  };

  const parseDateValue = (date: Date | string): Date | null => {
    if (date instanceof Date) {
      return Number.isNaN(date.valueOf()) ? null : toDateOnly(date);
    }

    const parsedDate = parseISO(date.split("T")[0]);
    return Number.isNaN(parsedDate.valueOf()) ? null : parsedDate;
  };

  // Keeps the original time (e.g. end-of-day) instead of truncating to midnight like parseDateValue does.
  // Strips the UTC offset so the literal date/time digits aren't shifted across the browser's local timezone.
  const parseExpirationDateValue = (date: Date | string): Date | null => {
    const parsedDate = date instanceof Date ? date : parseISO(date.replace(/(Z|[+-]\d{2}:?\d{2})$/, ""));
    return Number.isNaN(parsedDate.valueOf()) ? null : parsedDate;
  };

  const getPrefillActivationDate = (pmcId: number): Date => {
    const parkingProduct = parkingProducts.parkingProducts.data?.find((product) => product.pmcId === pmcId);
    const firstAvailableStartDate = parkingProduct?.firstAvailableContractStartDate;
    if (!firstAvailableStartDate) {
      return new Date();;
    }

    return parseDateValue(firstAvailableStartDate) ?? new Date();
  };

  const getPrefillExpirationDate = (pmcId: number): Date | null => {
    const expirationDate = parkingProducts.parkingProducts.data?.find((product) => product.pmcId === pmcId)?.expirationDate;
    return expirationDate ? parseExpirationDateValue(expirationDate) : null;
  };

  const initializeProductDates = (pmcId: number) => {
    if (!formik.values.productDates?.[pmcId]?.activationDate) {
      formik.setFieldValue(`productDates.${pmcId}.activationDate`, getPrefillActivationDate(pmcId));
    }
    if (!formik.values.productDates?.[pmcId]?.expirationDate) {
      const expirationDate = getPrefillExpirationDate(pmcId);
      if (expirationDate) {
        formik.setFieldValue(`productDates.${pmcId}.expirationDate`, expirationDate);
      }
    }
  };

  const getParkingProductsFromPmcIds = (pmcIds: number[], productDates?: Record<number, { activationDate: Date | null; expirationDate: Date | null }>) : ParkingProductModel[] =>
    parkingProducts.locatedParkingProducts.data
      ?.flatMap(l => l.parkingProducts)
      ?.filter(p => pmcIds.includes(p.pmcId!))
      .map(p => ({
        pmcId: p.pmcId!,
        seasonTicketId: p.id!,
        countryCode: p.countryCode!,
        contractStartDate: p.contractStartDate,
        activationDate: getLocalizedDate(productDates?.[p.pmcId!]?.activationDate ?? null, false),
        expirationDate: getLocalizedDate(productDates?.[p.pmcId!]?.expirationDate ?? null, true),
      })) || [];

  const handleSchedulingError = useCallback((pmcId: number, error: SchedulingDateRangePickerError | undefined) => {
    setSchedulingErrors((errors) => (
      errors[pmcId] === error
        ? errors
        : { ...errors, [pmcId]: error }
    ));
  }, []);

  const handleFormSubmit = (values: IVehicleFormValues) => {
    const hasSchedulingError = enableFleetVehicleScheduling && values.products?.some((pmcId) => (
      schedulingErrors[pmcId]
    ));
    if (hasSchedulingError) {
      return;
    }

    if (fleetManagerState.assignmentType === AssignmentType.Multiple) {
      const productSchedules: ProductSchedule[] = (values.products ?? []).map(pmcId => ({
        pmcId,
        activationDate: values.productDates?.[pmcId]?.activationDate ?? null,
        expirationDate: values.productDates?.[pmcId]?.expirationDate ?? null,
      }));
      setSelectedProducts(productSchedules);
      goToUploadFile();
      return;
    }

    if (isEditMode) {
      const updateRequest : UpdateVehicleRequestModel = {
        numberPlate: {
          countryCode: convertNumberPlatePrefixToIsoCountryCode(values.numberPlatePrefix).toLowerCase(),
          value: values.numberPlate.toUpperCase(),
        },
        parkingProducts: getParkingProductsFromPmcIds(values.products || [], values.productDates),
        description: values.description,
        isUnlimited: useBulkVehicleAssignments! && parkingProducts.hasUnlimitedEntryRights,
      };
      updateVehicleAssignment(
        appState.user.seasonTicketOwnerCrmId!,
        getSelectedVehicleAssignemntId(),
        updateRequest,
        () => {
          onClose();
          if (onRefresh) {
            onRefresh();
          }
        });
    } else {
      // lazily gen vehicleAssignmentId for next successful assign vehicle request
      if (!newVehicleAssignmentIdRef.current) {
        newVehicleAssignmentIdRef.current = crypto.randomUUID();
      }
      const assignRequest : AssignVehicleRequestModel = {
        numberPlate: {
          countryCode: convertNumberPlatePrefixToIsoCountryCode(values.numberPlatePrefix).toLowerCase(),
          value: values.numberPlate.toUpperCase(),
        },
        parkingProducts: getParkingProductsFromPmcIds(values.products || [], values.productDates),
        description: values.description,
        aggregateId: newVehicleAssignmentIdRef.current,
        isUnlimited: useBulkVehicleAssignments! && parkingProducts.hasUnlimitedEntryRights,
      };
      assignVehicle(appState.user.seasonTicketOwnerCrmId!, assignRequest, () => {
        // reset the vehicleAssignmentId
        newVehicleAssignmentIdRef.current = undefined;
        onClose();
        if (onRefresh) {
          onRefresh();
        }
      });
    }
  };

  const getSelectedVehicleAssignemntId = (): string => {
    const { vehicleAssignmentId } = parseAssignedParkingRowKey(fleetManagerState.selectedVehicleAssignment!.rowKey);
    return vehicleAssignmentId;
  }

  const onDeleteVehicleAssignment = () => {
      toggleDeleteVehicleAssignmentsWarning(fleetManagerState.selectedVehicleAssignment);
  }

  const topCountriesNumberPlatesDVA = new CountriesNumberPlates().getTopCountriesNumberPlatesDVA();

  const supportedNumberPlates = useMemo(() => {
    const countriesNumberPlates = new CountriesNumberPlates();
    const countries = countriesNumberPlates.getCountriesNumberPlates();

    const countryCode = getCountryCodeByLanguage(appState.selectedLanguage);
    const topCountriesNumberPlatesWithSelectedCountryTop =  moveCountryToTop(countryCode, topCountriesNumberPlatesDVA);

    let dvaCountries = topCountriesNumberPlatesWithSelectedCountryTop.concat(countries);

    return dvaCountries;
  }, []);

  const { validationSchema, formik } = useValidationSchema(
    t,
    isEditMode,
    handleFormSubmit,
    supportedNumberPlates[0].numberPlateCode
  );

  const fetchParkingProducts = () => {
    getParkingProducts(appState.user.seasonTicketOwnerCrmId as string, appState.selectedLanguage);
  };

  const initForm = async () => {
    fetchParkingProducts();
    setNumberPlateWarning(undefined);
    setNumberPlateError(undefined);
    blockVehicleConcurrentUpload.fetch();

    if (isEditMode) {
      await setFieldValues();
    }
  };

  const setFieldValues = (): Promise<void> => {
    return new Promise((resolve) => {
      getVehicleAssignment(getSelectedVehicleAssignemntId(), (vehicleAssignment: VehicleAssignmentModel) => {
        formik.setFieldValue(Fields.NumberPlatePrefix, convertIsoCountryCodeToNumberPlatePrefix(vehicleAssignment.vehicle.countryCode));
        formik.setFieldValue(Fields.NumberPlate, vehicleAssignment.vehicle.value);
        formik.setFieldValue(Fields.Description, vehicleAssignment.vehicle.description);
        formik.setFieldValue(Fields.Products, vehicleAssignment.assignedProducts.map(p => p.pmcId));

        vehicleAssignment.assignedProducts.forEach(p => {
          formik.setFieldValue(
            `productDates.${p.pmcId}.activationDate`,
            p.activationDate ? parseISO(p.activationDate.split('T')[0]) : getPrefillActivationDate(p.pmcId)
          );
          formik.setFieldValue(
            `productDates.${p.pmcId}.expirationDate`,
            p.expiryDate ? parseExpirationDateValue(p.expiryDate) : getPrefillExpirationDate(p.pmcId)
          );
          if (!p.expiryDate) {
            assignedProductIdsWithoutExpiryRef.current.push(p.pmcId);
          }
        });

        resolve();
      });
    });
  }

  useEffect(() => {
    setIsFormInitialized(false);

    let isMounted = true;
    initForm().finally(() => {
      if (isMounted) {
        setIsFormInitialized(true);
      }
    });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (getParkingProductsErrorTranslationKey() === "errors:noCapacity") {
      fetchParkingProducts();
      handleDeselectAll();
    }
  }, [parkingProducts.error?.errors]);

  useEffect(() => {
    assignedProductIdsWithoutExpiryRef.current.forEach((pmcId) => initializeProductDates(pmcId));
  }, [parkingProducts.parkingProducts.data]);

  const handleSelectAll = (productIds: number[]) => {
    productIds.forEach((pmcId) => initializeProductDates(pmcId));
    formik.setFieldValue(Fields.Products, productIds);
  };

  const handleDeselectAll = () => {
    formik.setFieldValue(Fields.Products, []);
  };

  const handleProductChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    parkingProducts: number[]
  ) => {
    const { value, checked } = event.currentTarget;
    const productId = parseInt(value);

    let products = [...parkingProducts];
    if (!checked) {
      products = without(products, productId);
    } else {
      products.push(productId);
      initializeProductDates(productId);
    }
    formik.setFieldValue(Fields.Products, products);
  };

  const onInputChange = (field: string, e?: React.ChangeEvent<any>) => {
    if (!isUndefined(formik.errors[field as keyof typeof formik.errors])) {
      formik.setFieldError(field, undefined);
    }

    if (e) {
      formik.handleChange(e);
    }
  };

  const getFormTitle = () => (isEditMode ? t("editExistingVehicleAssignment") : t("assignVehicle"));
  const goToUploadFile = (): void => {
    setVehicleDragAndDropView(true);
  };
  const submitButtonText = () => {
    if (fleetManagerState.assignmentType === AssignmentType.Multiple) {
      return t("goToUpload");
    }

    return !isEditMode ? t("assignVehicle") : t("updateVehicle");
  };

  const getParkingProductsErrorTranslationKey = (): string | undefined => {
    const error = parkingProducts.error;

    if (checkValidationError(error, errorConstants.keys.parkingRights, errorConstants.codes.notAvailable) ||
      checkValidationError(error, errorConstants.keys.entryRights, errorConstants.codes.notAvailable)) {
      return "errors:noCapacity";
    }

    if (checkValidationError(error, errorConstants.keys.numberPlate, errorConstants.codes.alreadyLinkedToAnotherUser)) {
      return "errors:alreadyLinkedToAnotherUser";
    }

    if (checkValidationError(error, errorConstants.keys.numberPlate, errorConstants.codes.licensePlateAlreadyLinked)) {
      return "errors:licensePlateAlreadyLinked";
    }

    if (checkValidationError(error, errorConstants.keys.parkingRights, errorConstants.codes.datesNotInContractBounds)) {
      return "vehicleDragAndDrop:datesNotInContractBounds";
    }

    if (checkValidationError(
      error,
      errorConstants.keys.parkingRights,
      errorConstants.codes.activationDateBeforePassCommercialStartDate
    )) {
      return "errors:activationDateBeforePassCommercialStartDate";
    }

    if (checkValidationError(error, errorConstants.keys.parkingRights, errorConstants.codes.quantityExceeded)) {
      return "vehicleDragAndDrop:quantityExceeded";
    }

    return undefined;
  };

  const parkingProductsErrorTranslationKey = getParkingProductsErrorTranslationKey();
  const setAssignmentTypeSingle = (): void => {
    blockVehicleConcurrentUpload.fetch();
    setAssignmentType(AssignmentType.Single);
  }
  const setAssignmentTypeMultiple = (): void => {
    blockVehicleConcurrentUpload.fetch();
    setAssignmentType(AssignmentType.Multiple);
  };

  const handleNumberPlatePrefixChange = (npPrefix: string) => {
    onInputChange(Fields.NumberPlatePrefix);
    formik.setFieldValue(Fields.NumberPlatePrefix, npPrefix);
    validateNumberPlateAndSetState(npPrefix);
  };

  const oldNpValue = React.useRef("");
  const handleNumberPlateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const np = e.target.value;
    const field = Fields.NumberPlate;
    if (oldNpValue.current !== np) {
      formik.setFieldValue(field, np);
      onInputChange(field, e);
      oldNpValue.current = np;
    }
  };

  const tNumberPlate = () => {
    const nb = t("globals:numberPlate");

    if (appState.selectedLanguage === 'de') {
      return nb;
    }

    return nb.toLowerCase();
  }

  const validateGermanPlate = (numberPlate: string) : ValidationResult => {
    if (formik.values.numberPlate === numberPlate || formik.values.numberPlate.split('-')[0].length === 0) {
        return {
          status: 'error',
          message: format(t("errors:required.field"), tNumberPlate())
        };
    }

    if (!numberPlateValidator.isValidGermanRegionNumber(formik.values.numberPlate.split('-')[0])) {
        return {
          status: 'error',
          message: t("errors:format.numberPlate"),
        };
    }

    if(formik.values.numberPlate.split('-').pop()?.length === 0) {
      return {
        status: 'error',
        message: t("errors:format.numberPlate"),
      };
    }

    if (!numberPlateValidator.isValidGermanNumberPlate(formik.values.numberPlate)) {
        return {
          status: 'error',
          message: t("errors:format.numberPlate"),
        };
    }

    return { status: 'valid' };
  };

  const validateOtherNumberPlate = (numberPlate: string) : ValidationResult => {
    if (numberPlate.length === 0) {
      return {
        status: 'error',
        message: format(t("errors:required.field"), tNumberPlate()),
      };
    }

    if (!numberPlateValidator.isValidNumberPlate(numberPlate)) {
      return {
        status: 'error',
        message: t("errors:format.numberPlate")
      };
    }

    return { status: 'valid' };
  }

  const getNumberPlate = (prefix: string) => {
    return prefix === 'D'
    ? formik.values.numberPlate.split('-').pop()! :
    formik.values.numberPlate.replace('-', '');
  }

  const validateNumberPlate = (numberPlatePrefix?: string) : ValidationResult | undefined => {
    let prefix = formik.values.numberPlatePrefix;

    if (numberPlatePrefix !== undefined) {
      prefix = numberPlatePrefix;

      if (formik.values.numberPlate.length === 0) {
        return;
      }
    }

    const numberPlate = getNumberPlate(prefix);

    if (prefix === 'D') {
      const germanResult = validateGermanPlate(numberPlate);
      if (germanResult.status !== 'valid') {
        return germanResult;
      }
    } else {
      const nbResult = validateOtherNumberPlate(numberPlate);
      if (nbResult.status !== 'valid') {
        return nbResult;
      }
    }

    if (numberPlate.length < 2) {
      return {
        status: 'warning',
        message: t("errors:minLength", { field: t("globals:numberPlate"), count: 2 })
      };
    }

    if (numberPlate.length > 7) {
      return {
        status: 'warning',
        message: t("errors:maxLength", { field: t("globals:numberPlate"), count: 7 }),
      };
    }

    return { status: 'valid' };
  };

  const setNumberPlateValidationResult = (result: ValidationResult) => {
    switch (result.status) {
      case 'error':
        setNrPlateError(result.message);
        break;
      case 'warning':
        setNrPlateWarning(result.message);
        break;
      case 'valid':
        setNumberPlateWarning(undefined);
        setNumberPlateError(undefined);
        break;
    }
  }

  const validateNumberPlateAndSetState = (numberPlatePrefix?: string) : ValidationResult | undefined => {
    const result = validateNumberPlate(numberPlatePrefix);
    if (!result) {
      return;
    }
    setNumberPlateValidationResult(result);
    return result;
  }

  const setNrPlateWarning = (warning: string) => {
    setNumberPlateError(undefined);
    setNumberPlateWarning(warning);
  }

  const setNrPlateError = (error: string) => {
    setNumberPlateWarning(undefined);
    setNumberPlateError(error);
  }

  const initialRightSelections : IRightSelection[] | undefined = useMemo(
    () =>
      isEditMode
        ? parkingProducts.selectedVehicleAssignment.data?.assignedProducts?.map(x => ({
            contractType: ContractType.SeasonTicket,
            pmcId : x.pmcId,
        }))
        : [],
    [isEditMode, parkingProducts.selectedVehicleAssignment]
  );

  const productsForVehicle = useMemo(() => parkingProducts.parkingProducts.data!.filter(p =>
    p.productType === ParkingProductType.ParkingRights &&
    p.contractType == ContractType.SeasonTicket ),
  [parkingProducts.parkingProducts.data]);

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={formik.initialValues}
      onSubmit={handleFormSubmit}
    >
        <div className="form-container">
          <div className="form-header">
            <Container className="form-header-content">
              {isEditMode && (
                <DeleteWrapper onClick={onDeleteVehicleAssignment}>
                  <DeleteDelegeeIcon />
                  <DeleteText>{t("deleteVehicle")}</DeleteText>
                </DeleteWrapper>
              )}
              <Heading width="auto" justifyContent="center" fontSize={typography.fontSizeLarge}>
                {getFormTitle()}
              </Heading>
              <Button onClick={onClose} classes={{ root: "close-icon-btn" }} disableRipple>
                <CloseOutlined classes={{ root: "close-icon" }} />
              </Button>
            </Container>
          </div>
          <Box component="form" sx={{ flexGrow: 1, paddingY: 2 }}>
            {!isEditMode && !parkingProducts.hasUnlimitedEntryRights && (
              <Container>
                <NoPaddingBottomGrid
                  container
                  direction="row"
                  justifyContent="flex-start"
                  alignItems="center"
                >
                  <NoPaddingBottomGrid container item direction="column" xs={12}>
                    <div>
                      <div>
                        <ButtonGroup variant="contained" fullWidth disableRipple>
                          <Button
                            onClick={setAssignmentTypeSingle}
                            color={
                              fleetManagerState.assignmentType === AssignmentType.Single
                                ? "primary"
                                : "default"
                            }
                            disableElevation
                          >
                            {t("single")}
                          </Button>
                          <Button
                            onClick={setAssignmentTypeMultiple}
                            color={
                              fleetManagerState.assignmentType === AssignmentType.Multiple
                                ? "primary"
                                : "default"
                            }
                            disableElevation
                          >
                            {t("multiple")}
                          </Button>
                        </ButtonGroup>
                      </div>
                    </div>
                  </NoPaddingBottomGrid>
                </NoPaddingBottomGrid>
              </Container>
            )}
            <div className="vehicle-form-body">
              <Container>
                {fleetManagerState.assignmentType == AssignmentType.Single && (
                  <React.Fragment>
                    <Grid container direction="row" justifyContent="flex-start" alignItems="center">
                      <Heading fontSize={typography.fontSizeBase}>
                        <strong>{t("accessInformation")}</strong>
                      </Heading>
                    </Grid>
                    <Grid container direction="row" justifyContent="space-between" alignItems="center">
                      <Grid item container direction="column" xs={3}>
                        <RequiredInputLabel htmlFor={Fields.NumberPlate}>{t("globals:numberPlate")}</RequiredInputLabel>
                      </Grid>
                      <Grid item container direction="column" xs={9}>
                        {isEditMode ? fleetManagerState.selectedVehicleAssignment?.numberPlate :
                          <NumberPlateDeSupport
                            id={Fields.NumberPlate}
                            name={Fields.NumberPlate}
                            placeholder={t("visitors:visitorDetailsForm.numberPlatePlaceholder")}
                            regionPlaceholder={t("visitors:visitorDetailsForm.numberPlateRegionPlaceholder")}
                            onValueChange={handleNumberPlateChange}
                            validateNumberPlate={validateNumberPlateAndSetState}
                            onPrefixSelect={handleNumberPlatePrefixChange}
                            error={fleetManagerState.numberPlateError}
                            warning={fleetManagerState.numberPlateWarning}
                            value={formik.values.numberPlate}
                            prefix={formik.values.numberPlatePrefix}
                            numberToSkipOnKeyPress={topCountriesNumberPlatesDVA.length - 1}
                            supportedNumberPlates={supportedNumberPlates.map((s) => ({
                              numberPlatePrefix: s.numberPlateCode,
                              countryCode: s.countryCode,
                            }))}
                            setFieldError={formik.setFieldError}
                            setFieldValue={formik.setFieldValue}
                        />}
                      </Grid>
                    </Grid>
                    <Grid container direction="row" justifyContent="space-between" alignItems="center">
                      <Grid container direction="column" xs={3} item>
                        <InputLabel htmlFor={Fields.Description}>{t("description")}</InputLabel>
                      </Grid>
                      <Grid container direction="column" xs={9} item>
                        <CustomInput
                          id={Fields.Description}
                          name={Fields.Description}
                          autoComplete="off"
                          value={formik.values.description}
                          onChange={formik.handleChange}
                          classes={{ root: "form-input" }}
                          variant="outlined"
                          placeholder={t("descriptionPlaceholder")}
                          error={Boolean(formik.errors.description)}
                        />
                        <MuiHelperText error={formik.errors.description} />
                      </Grid>
                    </Grid>
                    <Spacer />
                  </React.Fragment>
                )}
                  <Translation ns={["vehicleForm", "delegeeForm"]} nsMode="fallback">
                    {(t) => (
                      <ParkingRightsGrid
                        data={productsForVehicle}
                        initiallySelectedRights={initialRightSelections}
                        disableProductsWithoutFirstAvailableContractStartDate={enableFleetVehicleScheduling}
                        loading={parkingProducts.parkingProducts.loading || (isEditMode ? parkingProducts.selectedVehicleAssignment.loading : false)}
                        onChange={(event) => handleProductChange(event, formik.values.products ?? [])}
                        onStateChange={prs => handleSelectAll(prs.filter(pr => pr.checked).map(pr => pr.id))}
                        onSelectAll={(ids) => handleSelectAll(ids.map(x => x.pmcId))}
                        onDeselectAll={handleDeselectAll}
                        error={{
                          hasError: Boolean(formik.errors.products),
                          message: formik.errors.products,
                        }}
                        title={t("selectProduct")}
                        required={!isEditMode}
                        selectAllTooltipText={t("selectTooltip")}
                        deselectAllTooltipText={t("deselectTooltip")}
                        selectAllText={t("selectAll")}
                        deselectAllText={t("deselectAll")}
                        renderRowExpansion={enableFleetVehicleScheduling
                          ? (pr) => {
                              const pmcId = pr.right!.pmcId!;
                              const assignedProduct = isEditMode
                                ? parkingProducts.selectedVehicleAssignment.data?.assignedProducts.find(
                                  (product) => product.pmcId === pmcId
                                )
                                : undefined;
                              const prefillActivationDate = getPrefillActivationDate(pmcId);
                              const defaultActivationDate = assignedProduct?.activationDate
                                ? parseDateValue(assignedProduct.activationDate) ?? prefillActivationDate
                                : prefillActivationDate;
                              const prefillExpirationDate = getPrefillExpirationDate(pmcId);
                              const defaultExpirationDate = assignedProduct?.expiryDate
                                ? parseExpirationDateValue(assignedProduct.expiryDate) ?? prefillExpirationDate
                                : prefillExpirationDate;

                              return isFormInitialized && (
                                <SchedulingDateRangePicker
                                  defaultActivationDate={defaultActivationDate}
                                  defaultExpirationDate={defaultExpirationDate}
                                  prefillActivationDate={prefillActivationDate}
                                  prefillExpirationDate={prefillExpirationDate}
                                  activationDateLabel={t("vehicleForm:activationDate")}
                                  expirationDateLabel={t("vehicleForm:expirationDate")}
                                  onActivationDateChange={(date) => formik.setFieldValue(`productDates.${pmcId}.activationDate`, date)}
                                  onExpirationDateChange={(date) => formik.setFieldValue(`productDates.${pmcId}.expirationDate`, date)}
                                  onError={(error) =>
                                    handleSchedulingError(pmcId, error)
                                  }
                                />
                              );
                            }
                          : undefined
                        }
                      />
                    )}
                  </Translation>
                {blockVehicleConcurrentUpload.blocked && <VehicleConcurrentUploadWarning />}
              </Container>
            </div>
          </Box>
          <CustomError
            show={Boolean(parkingProductsErrorTranslationKey)}
            message={parkingProductsErrorTranslationKey ? t(parkingProductsErrorTranslationKey) : ""}
          />
          <div className="form-footer">
            <Container className="form-footer-content">
              <React.Fragment>
                <CustomButton.Light onClick={onClose} text={t("globals:close")} id="close-invite" />
                <CustomButton.Primary
                  loading={parkingProducts.loading}
                  onClick={(e) => {
                    e?.preventDefault();
                    if (fleetManagerState.assignmentType === AssignmentType.Multiple) {
                      formik.handleSubmit();
                      return;
                    }
                    if (fleetManagerState.numberPlateError) {
                      formik.validateForm();
                      return;
                    }
                    const res = validateNumberPlateAndSetState();
                    if (res !== undefined && res.status === 'error') {
                      formik.validateForm();
                      return;
                    }
                    formik.handleSubmit();
                  }}
                  text={submitButtonText()}
                  id="submit-invite"
                  disabled={blockVehicleConcurrentUpload.blocked || Boolean(
                    formik.values.products?.some((pmcId) => schedulingErrors[pmcId])
                  )}
                />
              </React.Fragment>
            </Container>
          </div>
        </div>
    </Formik>
  );
};

const mapStateToProps = (state: RootReducer) => {
  const { parkingProducts } = state;

  return {
    parkingProducts
  };
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  disposeError: () => dispatch(parkingProductsActions.disposeError()),
  getParkingProducts: (seasonTicketOwnerCrmId: string, location: string,) => dispatch(parkingProductsThunks.getParkingProducts({ seasonTicketOwnerCrmId, location })),
  getVehicleAssignment: (aggregateId: string, onSuccess?: (res: any) => void) => dispatch(parkingProductsActions.getVehicleAssignment(aggregateId, onSuccess)),
  assignVehicle: (seasonTicketOwnerCrmId: string, req: AssignVehicleRequestModel, onSuccess?: () => void) => dispatch(parkingProductsActions.assignVehicle(seasonTicketOwnerCrmId, req, onSuccess)),
  updateVehicleAssignment: (seasonTicketOwnerCrmId: string, aggregateId: string, req: UpdateVehicleRequestModel, onSuccess?: () => void) => dispatch(parkingProductsActions.updateVehicleAssignment(seasonTicketOwnerCrmId, aggregateId, req, onSuccess)),
});

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

export default connect(mapStateToProps, mapDispatchToProps)(VehicleForm);
